import { defineStore } from "pinia";
import { ref, shallowRef, markRaw } from "vue";
import { io } from "socket.io-client";

// Reconnect gaps shorter than this are covered by Socket.IO connection-state
// recovery (enabled server-side). Longer gaps may have lost broadcasts, so the
// store asks registered handlers to pull authoritative state (TODO 3.1).
export const RESYNC_DISCONNECT_GAP_MS = 5000;

// Attempts before we start logging loudly. Reconnection itself is NEVER given
// up on: a finite `reconnectionAttempts` used to let socket.io permanently
// stop retrying after ~90s of downtime (10 attempts, 1s→10s backoff). An
// always-on surface such as the LED display then sat on a dead socket for the
// rest of the show and only refreshed on the slow safety-net tick, which is
// what made admin actions appear up to a minute late.
const RECONNECT_WARN_AFTER_ATTEMPTS = 10;
// Backoff ceiling. Kept well below the previous 10s so a blip during a live
// show recovers in seconds.
const RECONNECT_DELAY_MAX_MS = 5000;

export const useSocketStore = defineStore("socket", () => {
  // shallowRef + markRaw: a plain `ref` makes Vue deeply proxy the entire
  // socket.io client (manager, engine, transport, packet buffers), so every
  // internal mutation on a hot path pays reactivity overhead. Nothing renders
  // socket internals — only `isConnected` is displayed.
  const socket = shallowRef(null);
  const isConnected = ref(false);
  const reconnectAttempts = ref(0);
  const maxReconnectAttempts = RECONNECT_WARN_AFTER_ATTEMPTS;
  const currentEventId = ref(null);

  // Timestamp of the most recent unexpected disconnect. Cleared on intentional
  // teardown (`disconnect()`) so navigating away does not force a resync the
  // next time a page connects.
  let disconnectedAt = null;
  // Handlers invoked after a reconnect whose disconnect gap exceeded
  // RESYNC_DISCONNECT_GAP_MS. Views register here so the socket store does not
  // import the votes store (votes.js already imports this store).
  const resyncHandlers = new Set();

  const notifyResyncNeeded = (eventId, gapMs) => {
    resyncHandlers.forEach((handler) => {
      try {
        handler(eventId, gapMs);
      } catch (error) {
        console.error("Socket resync handler failed:", error);
      }
    });
  };

  // Register a reconnect-resync callback. Returns an unsubscribe function.
  const onResyncNeeded = (handler) => {
    if (typeof handler !== "function") {
      return () => {};
    }
    resyncHandlers.add(handler);
    return () => {
      resyncHandlers.delete(handler);
    };
  };

  // ── Browser-level recovery nudges ──────────────────────────────────────
  // socket.io's backoff can leave a display waiting out a timer long after the
  // network is actually back. Waking the tab or regaining connectivity forces
  // an immediate attempt.
  const forceReconnect = () => {
    if (socket.value && !socket.value.connected) {
      socket.value.connect?.();
    }
  };

  const onWindowOnline = () => forceReconnect();
  const onVisible = () => {
    if (typeof document !== "undefined" && document.visibilityState === "visible") {
      forceReconnect();
    }
  };

  let recoveryListenersAttached = false;

  const attachRecoveryListeners = () => {
    if (recoveryListenersAttached || typeof window === "undefined") {
      return;
    }
    window.addEventListener("online", onWindowOnline);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisible);
    }
    recoveryListenersAttached = true;
  };

  const detachRecoveryListeners = () => {
    if (!recoveryListenersAttached || typeof window === "undefined") {
      return;
    }
    window.removeEventListener("online", onWindowOnline);
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", onVisible);
    }
    recoveryListenersAttached = false;
  };

  const connect = () => {
    // Idempotent on the *instance*, not on `connected`. Guarding on
    // `connected` alone meant a second call made while the very first
    // handshake was still in flight (LedView/EventVoting both call connect()
    // right after their awaited fetch) silently built a SECOND io() manager
    // and overwrote this ref. The orphaned first socket stayed connected for
    // the lifetime of the page, never joined an event room, and doubled the
    // connection count for every open display.
    if (socket.value) {
      // Existing instance that merely dropped: kick it instead of leaking a
      // replacement, and skip socket.io's remaining backoff wait.
      if (!socket.value.connected) {
        socket.value.connect?.();
      }
      return;
    }

    const socketUrl = import.meta.env.VITE_SOCKET_URL || "http://localhost:5000";

    socket.value = markRaw(
      io(socketUrl, {
        transports: ["websocket", "polling"],
        // Reconnection settings - CRITICAL for reliability
        reconnection: true,
        // Never stop trying. A long-lived display (LED wall) must recover from
        // a deploy or venue network outage of any length without a manual
        // page reload.
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000, // Start with 1 second
        reconnectionDelayMax: RECONNECT_DELAY_MAX_MS,
        randomizationFactor: 0.5, // Add randomness to prevent thundering herd
        timeout: 20000, // 20 second connection timeout
        upgradeTimeout: 10000 // 10 second upgrade timeout
      })
    );

    socket.value.on("connect", () => {
      isConnected.value = true;
      reconnectAttempts.value = 0;
      console.log("✅ Socket connected");

      // Re-join event room if we were previously in one
      if (currentEventId.value) {
        console.log(`🔄 Re-joining event room: ${currentEventId.value}`);
        socket.value.emit("join_event", { eventId: currentEventId.value });
      }

      // TODO 3.1: after a long disconnect, ask views to pull authoritative
      // event + vote state. Short blips rely on connection-state-recovery.
      const gapMs = disconnectedAt !== null ? Date.now() - disconnectedAt : 0;
      disconnectedAt = null;
      if (gapMs >= RESYNC_DISCONNECT_GAP_MS && currentEventId.value) {
        console.log(`🔄 Disconnect gap ${gapMs}ms — requesting authoritative resync`);
        notifyResyncNeeded(currentEventId.value, gapMs);
      }
    });

    socket.value.on("disconnect", (reason) => {
      isConnected.value = false;
      disconnectedAt = Date.now();
      console.log("⚠️ Socket disconnected:", reason);

      // If server disconnected us, manually reconnect
      if (reason === "io server disconnect") {
        console.log("🔄 Server disconnected, attempting to reconnect...");
        socket.value.connect();
      }
    });

    socket.value.on("connect_error", (error) => {
      reconnectAttempts.value++;
      console.error("❌ Socket connection error:", error.message);

      if (reconnectAttempts.value === maxReconnectAttempts) {
        console.warn(
          `⚠️ Socket still unreachable after ${maxReconnectAttempts} attempts — retrying indefinitely`
        );
      }
    });

    socket.value.on("reconnect", (attemptNumber) => {
      console.log(`✅ Socket reconnected after ${attemptNumber} attempts`);
      isConnected.value = true;
      reconnectAttempts.value = 0;
    });

    socket.value.on("reconnect_attempt", (attemptNumber) => {
      console.log(`🔄 Socket reconnection attempt ${attemptNumber}`);
    });

    socket.value.on("reconnect_failed", () => {
      // Should be unreachable with reconnectionAttempts: Infinity, but if the
      // manager ever does give up, restart it rather than going dark.
      console.error("❌ Socket reconnection failed — restarting the connection");
      socket.value?.connect?.();
    });

    attachRecoveryListeners();
  };

  const disconnect = () => {
    detachRecoveryListeners();
    if (socket.value) {
      socket.value.disconnect();
      socket.value = null;
      isConnected.value = false;
      currentEventId.value = null;
      disconnectedAt = null;
    }
  };

  const joinEvent = (eventId) => {
    currentEventId.value = eventId;
    if (socket.value && socket.value.connected) {
      socket.value.emit("join_event", { eventId });
      console.log(`📍 Joined event room: ${eventId}`);
    }
  };

  const leaveEvent = (eventId) => {
    if (socket.value && socket.value.connected) {
      socket.value.emit("leave_event", { eventId });
    }
    if (currentEventId.value === eventId) {
      currentEventId.value = null;
    }
  };

  const emit = (event, data) => {
    if (socket.value && socket.value.connected) {
      socket.value.emit(event, data);
    }
  };

  const on = (event, callback) => {
    if (socket.value) {
      socket.value.on(event, callback);
    }
  };

  const off = (event, callback) => {
    if (socket.value) {
      socket.value.off(event, callback);
    }
  };

  return {
    socket,
    isConnected,
    connect,
    disconnect,
    forceReconnect,
    joinEvent,
    leaveEvent,
    emit,
    on,
    off,
    onResyncNeeded
  };
});
